import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import {
  Divider,
  FormControl,
  FormHelperText,
  TextField,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { Ticket, TicketStatus } from "@services/tickets/types";
import Chip from "@common/components/Chips";
import Button from "@common/components/Button";
import TicketDetails from "./Details";
import TicketAdvancedDetails, { TicketsType } from "./AdvancedDetails";
import Timeline from "./Timeline";
import TalServiceNow from "./TalServiceNow";
import Troubleshooting from "./Troubleshooting";
import CloseCaseDialog from "../CloseCaseDialog";
import ReopenCaseDialog from "../ReopenCaseDialog";
import MeetingAffected from "./MeetingAffected";
import Comments from "./Comments";
import Attachments from "./Attachments";
import CopyLink from "@common/components/Button/CopyLink";
import { useErrorNotification } from "@common/components/Notification";
import { FormContext, TimeValue } from "../context/formContext";
import { TicketListContext } from "@pages/Tickets/context";
import { putTicket } from "@services/tickets";
import Resolution from "./Resolution";
import Typography from "@common/components/Typography";
import { Priority } from "@services/account/types";
import { useParams } from "react-router-dom";
import { getDevice } from "@services/devices";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { getMeetingById } from "@services/meetings";
import { TicketExpandingDrawerContext } from "../TicketExpandingDrawer";
import { canViewDevice } from "@common/utils/user";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@common/components/IconButton";
export type Colors = "Critical" | "Major" | "Minor" | "Informational";

interface DrawerContentProps {
  onSave?: (b: boolean) => void;
}

const DrawerContent = (props: DrawerContentProps) => {
  const {
    dispatchTicket,
    dispatchDetails,
    dispatchMeeting,
    form,
    canEdit,
    isValid,
    timeOfIssue,
  } = useContext(FormContext);
  const [user] = useAuthenticatedUser();
  const { ticket, meeting } = form;
  const dispatchError = useErrorNotification();
  const { dispatch, isNewTicket } = useContext(TicketListContext);
  const { onSave = (b: boolean) => {} } = props;
  const [extend, setExtend] = useState<boolean>(false);
  const { t } = useTranslation();
  const [openCloseCase, setOpenCloseCase] = useState<boolean>(false);
  const [openReopenCase, setOpenReopenCase] = useState<boolean>(false);
  const disabled = ticket.status === "Closed" || !canEdit;
  const params = useParams();
  const { animate } = useContext(TicketExpandingDrawerContext);
  const fetchDevice = async (deviceId: string) => {
    const response = await getDevice(deviceId);
    dispatchTicket({
      device: response,
      room: response.room,
      account: response?.room?.location?.account,
      priority: Priority.Critical,
      openDate: Date.now(),
      lastUpdatedDate: Date.now(),
      status: TicketStatus.Open,
      assignedTo: user?.email,
    });
    dispatchDetails({ requester: user?.email });
  };

  const fetchMeeting = async (meetingId: string) => {
    const response = await getMeetingById(meetingId);
    dispatchMeeting(response);
    dispatchTicket({
      meetings: [
        ...(ticket?.meetings ?? []),
        {
          confirmationNumber: response.meetingId,
          title: response.title,
          meetingInstanceId: response.id,
        },
      ],
      account: response?.account,
      priority: Priority.Critical,
      openDate: Date.now(),
      lastUpdatedDate: Date.now(),
      status: TicketStatus.Open,
      assignedTo: user?.email,
    });
    dispatchDetails({ requester: user?.email });
  };

  useEffect(() => {
    if (isNewTicket && params.deviceid) {
      fetchDevice(params.deviceid);
    }
  }, [params.deviceid]);

  useEffect(() => {
    if (isNewTicket && params.meetingid) {
      fetchMeeting(params.meetingid);
    }
  }, [params.meetingid]);

  useLayoutEffect(() => {
    animate(extend);
  }, [extend]);

  const closeCase = () => {
    setOpenCloseCase(true);
  };

  const reopenCase = () => {
    setOpenReopenCase(true);
  };

  const handleSave = async () => {
    try {
      if (ticket) {
        const events = form.details?.events ?? [];
        if (
          timeOfIssue.type !== TimeValue.NoClue &&
          form.ticket.id === undefined
        )
          events.push({
            createDate:
              timeOfIssue.type === TimeValue.FewSeconds
                ? Date.now()
                : timeOfIssue.date,
            priority: form.ticket.priority,
            subject: form.ticket.issue?.issue,
            description: form.details.description,
          });
        await putTicket({
          ...form.ticket,
          details: {
            ...form.details,
            events,
          },
        } as Ticket);
      }
      dispatch("updateTicket");
      onSave(true);
    } catch (error) {
      dispatchError(error);
    }
  };

  const priorityColor = ticket.priority?.toString() as Colors | undefined;

  return (
    <>
      <Grid
        container
        wrap="nowrap"
        sx={{ width: '100%', flexDirection: "column",
          flex: "1",
          overflow: "hidden", }}
      >
        <Grid
          container
          sx={(theme) => ({
            alignItems: "center",
            padding: theme.spacing(1, 1.5),
            backgroundColor:
              priorityColor !== undefined
                ? theme.palette.ticket[priorityColor].dark
                : null,
          })}
          spacing={2}
        >
          <Grid>
            {isNewTicket ? (
              <Typography
                size="small"
                sx={(theme) => ({
                  color:
                    priorityColor !== undefined
                      ? theme.palette.ticket[priorityColor].light
                      : theme.palette.text.primary,
                })}
              >
                {t("New Ticket")}
              </Typography>
            ) : (
              <Chip
                radius="small"
                color={priorityColor ?? "white"}
                label={ticket.id}
              />
            )}
          </Grid>
          <Grid size={8}>
            <FormControl
              fullWidth
              size="small"
              sx={(theme) => ({
                width: "100%",
                "& .MuiFormHelperText-root": {
                  color: theme.palette.error.main,
                },
                "& .MuiInputLabel-root::after": {
                  content: "'*'",
                  color: theme.palette.error.main,
                },
              })}
            >
              <Tooltip title={ticket.subject ?? ""}>
                <TextField
                  label={t("Subject")}
                  value={ticket.subject ?? ""}
                  fullWidth
                  disabled
                />
              </Tooltip>
            </FormControl>
          </Grid>
          {isNewTicket ? (
            <Grid>
              <Button
                onClick={handleSave}
                disabled={!isValid}
                color={priorityColor}
                variant="outlined"
              >
                {t("Add Ticket")}
              </Button>
            </Grid>
          ) : (
            <>
              <Grid>
                <CopyLink
                  path={ticket.id ? `/tickets/${ticket.id}` : undefined}
                  variant="outlined"
                  sx={{
                    color: ({ palette }) =>
                      priorityColor
                        ? palette.ticket[priorityColor].main
                        : palette.common.white,
                    borderColor: ({ palette }) =>
                      priorityColor
                        ? palette.ticket[priorityColor].main
                        : palette.common.white,
                  }}
                />
              </Grid>
              <Grid>
                <Typography
                  size="small"
                  sx={{
                    color: ({ palette }) =>
                      priorityColor
                        ? palette.ticket[priorityColor].main
                        : palette.common.white,
                  }}
                >
                  {ticket.status == "Open"
                    ? t("Open")
                    : ticket.status == "Closed"
                    ? t("Closed")
                    : t("Pending Close")}
                </Typography>
              </Grid>
              <Grid>
                {!disabled ? (
                  <Button
                    onClick={closeCase}
                    color={priorityColor}
                    variant="outlined"
                  >
                    {t("Close Ticket")}
                  </Button>
                ) : (
                  canEdit && (
                    <Button
                      onClick={reopenCase}
                      color={priorityColor}
                      variant="outlined"
                    >
                      {t("Reopen Ticket")}
                    </Button>
                  )
                )}
              </Grid>
              {!disabled && (
                <Grid>
                  <Button
                    onClick={handleSave}
                    disabled={!isValid}
                    color={priorityColor}
                    variant="outlined"
                  >
                    {t("Update Ticket")}
                  </Button>
                </Grid>
              )}
            </>
          )}
          <Grid sx={(theme) => ({ position: "absolute", right: theme.spacing(2) })}>
            <IconButton
              onClick={() => {
                onSave(false);
              }}
              sx={{
                color: ({ palette }) =>
                  priorityColor
                    ? palette.ticket[priorityColor].main
                    : palette.common.white,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Grid
          container
          wrap="nowrap"
          sx={{ width: '100%', flex: "1",
            flexDirection: "column",
            overflowY: "scroll", }}
        >
          <TicketDetails
            devices={ticket.room?.devices?.map((x) => {
              const { devices, ...room } = ticket.room ?? {};
              return { ...x, room };
            })}
            device={ticket.device}
            meeting={meeting}
          />
          {!extend && (
            <Grid
              container
              sx={{ width: '100%', justifyContent: "center", }}
            >
              <Button
                variant="text"
                color="greyed"
                onClick={() => setExtend(true)}
              >
                {t("More details")}
              </Button>
            </Grid>
          )}
          {extend && (
            <Grid
              container
              sx={{ width: '100%', flexDirection: "column",
                flex: "1", }}
            >
              <Divider flexItem />
              <TicketAdvancedDetails
                ticketType={
                  params.deviceid ? TicketsType.Device : TicketsType.Meeting
                }
              />
              <Divider flexItem />
              <MeetingAffected />
              <Divider flexItem />
              <Resolution></Resolution>
              <Divider flexItem />
              <Comments />
              <Divider flexItem />
              <Timeline />
              {!isNewTicket && (
                <>
                  <Divider flexItem />
                  <Attachments></Attachments>
                  {ticket.details?.externalTicket !== undefined && (
                    <React.Fragment>
                      <Divider flexItem />
                      <TalServiceNow
                        externalTicket={ticket.details?.externalTicket}
                      />
                    </React.Fragment>
                  )}
                </>
              )}
              {canViewDevice(user) && (
                <>
                  <Divider flexItem />
                  <Troubleshooting />
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
      <CloseCaseDialog
        open={openCloseCase}
        onClose={() => setOpenCloseCase(false)}
      />
      <ReopenCaseDialog
        open={openReopenCase}
        onClose={() => setOpenReopenCase(false)}
      />
    </>
  );
};

export default DrawerContent;
